import { useRef, useMemo, useEffect, useState } from 'react'
import { isEqual } from 'lodash'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts/highstock'
import useTimeRange from 'src/contexts/timeRange'
import { GqlErrorScoreKind, GqlThresholdKind, Anomaly } from 'src/services'
import { ExcludedTimePeriod } from 'src/types/chartTypes'
import { getGraphColor } from 'src/utility/graphUtils/graphColors'
import {
  useAnomaly,
  useAnomalyAreas,
  useCursor,
  useFilterAreas,
  useNowLine,
  useThreshold,
  useHeightListener,
  useLoading,
  useZoom,
} from './utils'
import { Break, PlotLine, baseChartConfig } from './baseChartConfig'

interface Data {
  data: any
  name?: string
  color?: string
  hideArea?: boolean
}

interface TimeSeriesChartProps {
  // TODO jeff 2025-05-09
  // what is this for? It is passed by `src/pages/site/models/create/steps/Output/components/TargetValueInput.tsx`
  // But does not appear to be used for anything
  id?: any
  data: Data[]
  isPending: boolean
  tooltip: boolean
  threshold?: number
  staticTimeRange?: boolean
  yAxis?: Highcharts.YAxisOptions
  breaks?: Break[]
  // TODO jeff 2025-05-09
  // typing issues
  setYAxisRange?: any
  desaturate?: boolean
  anomaly?: Anomaly
  yAxisPerData?: boolean
  tooltipPerData?: boolean
  thresholdType?: GqlThresholdKind
  errorScoreKind?: GqlErrorScoreKind
  plotLines?: PlotLine[]
  excludedTimePeriods?: ExcludedTimePeriod[]
}

export function TimeSeriesChart({
  id,
  data,
  isPending,
  tooltip: tooltipEnabled,
  threshold,
  thresholdType,
  errorScoreKind,
  staticTimeRange,
  yAxis = {},
  breaks = [],
  setYAxisRange = () => {},
  desaturate = false,
  anomaly,
  yAxisPerData,
  tooltipPerData,
  plotLines,
  excludedTimePeriods,
}: TimeSeriesChartProps): JSX.Element {
  const ref = useRef<HighchartsReact.RefObject>(null)
  // default height to 256
  const { height = 256, wrapperRef } = useHeightListener()
  const { timeRange, setTimeRange } = useTimeRange()
  useZoom({ ref, ...timeRange })
  useCursor({ ref, height, withTrackballs: true })
  useLoading({ ref, loading: isPending, data })
  useThreshold({ ref, threshold })
  useFilterAreas({ ref, desaturate })
  useNowLine({ ref })
  useAnomaly({ ref, anomaly })
  useAnomalyAreas({ ref })

  const [counterHack, setCounterHack] = useState(0)
  const oldData = useRef(data)
  useEffect(() => {
    if (!isEqual(data, oldData.current) && !isPending) {
      oldData.current = data
      setCounterHack(c => c + 1)
    }
  }, [data, isPending])

  const firstData = (() => {
    try {
      return data[0].data[0][1]
    } catch {
      return undefined
    }
  })()

  const chart = useMemo(
    () => (
      <div
        className="h-full"
        style={{ transform: 'translateZ(0)' }}
        ref={wrapperRef}
      >
        <HighchartsReact
          ref={ref}
          highcharts={Highcharts}
          options={baseChartConfig({
            data,
            setTimeRange,
            height,
            tooltipEnabled,
            staticTimeRange,
            threshold,
            thresholdType,
            errorScoreKind,
            yAxis,
            breaks,
            setYAxisRange,
            getGraphColor,
            yAxisPerData,
            tooltipPerData,
            plotLines,
            excludedTimePeriods,
          })}
        />
      </div>
    ),
    // TODO: Not good
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      plotLines,
      counterHack,
      excludedTimePeriods,
      id,
      ref,
      wrapperRef,
      height,
      staticTimeRange,
      threshold,
      thresholdType,
      yAxis.min,
      yAxis.max,
      firstData,
    ],
  )

  return chart
}
